import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface MailboxBounceData {
  email: string
  from_name: string
  client_name: string
  domain: string
  bounces_today: number
  bounces_7d: number
  bounces_14d: number
  bounce_rate: number
  last_bounce_time: string | null
  total_bounces: number
}

export function useMailboxBounceAnalytics() {
  return useQuery({
    queryKey: ['mailbox-bounce-analytics'],
    queryFn: async (): Promise<MailboxBounceData[]> => {
      return await fetchBounceAnalyticsManually()
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  })
}

async function fetchBounceAnalyticsManually(): Promise<MailboxBounceData[]> {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  
  const fourteenDaysAgo = new Date(today)
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)

  // Get bounce events for different time periods
  const { data: bounceEvents, error: bounceError } = await supabase
    .from('obsync_dynamic_reports')
    .select('from_email, created_at, event_type')
    .eq('event_type', 'EMAIL_BOUNCE')
    .gte('created_at', fourteenDaysAgo.toISOString())
    .order('created_at', { ascending: false })

  if (bounceError) {
    console.error('Error fetching bounce events:', bounceError)
    return []
  }

  // Get mailbox data - no join since there's no relation
  const { data: mailboxes, error: mailboxError } = await supabase
    .from('mailboxes')
    .select('email, from_name, domain, client_id')

  if (mailboxError) {
    console.error('Error fetching mailboxes:', mailboxError)
    return []
  }

  // Process bounce data by mailbox
  const bouncesByMailbox = new Map<string, {
    today: number
    last7d: number
    last14d: number
    lastBounceTime: string | null
    totalBounces: number
  }>()

  bounceEvents?.forEach(event => {
    if (!event.from_email) return
    
    const eventDate = new Date(event.created_at)
    const email = event.from_email.toLowerCase()
    
    if (!bouncesByMailbox.has(email)) {
      bouncesByMailbox.set(email, {
        today: 0,
        last7d: 0,
        last14d: 0,
        lastBounceTime: null,
        totalBounces: 0
      })
    }
    
    const bounceData = bouncesByMailbox.get(email)!
    
    // Count total bounces (all bounces regardless of time)
    bounceData.totalBounces++
    
    // Count bounces for different periods
    if (eventDate >= today) {
      bounceData.today++
    }
    if (eventDate >= sevenDaysAgo) {
      bounceData.last7d++
    }
    if (eventDate >= fourteenDaysAgo) {
      bounceData.last14d++
    }
    
    // Track most recent bounce
    if (!bounceData.lastBounceTime || eventDate > new Date(bounceData.lastBounceTime)) {
      bounceData.lastBounceTime = event.created_at
    }
  })

  // Combine mailbox data with bounce analytics
  const result: MailboxBounceData[] = []
  
  mailboxes?.forEach(mailbox => {
    const bounceData = bouncesByMailbox.get(mailbox.email.toLowerCase())
    
    // Only include mailboxes that have bounces today or recently
    if (bounceData && (bounceData.today > 0 || bounceData.last7d > 0)) {
      result.push({
        email: mailbox.email,
        from_name: mailbox.from_name || '',
        client_name: (mailbox as any).client_id?.toString() || '',
        domain: mailbox.domain,
        bounces_today: bounceData.today,
        bounces_7d: bounceData.last7d,
        bounces_14d: bounceData.last14d,
        bounce_rate: 0, // Calculate from bounce events
        last_bounce_time: bounceData.lastBounceTime,
        total_bounces: bounceData.totalBounces
      })
    }
  })

  // Sort by total bounces (highest first), then by today's bounces, then by 7-day bounces
  return result.sort((a, b) => {
    if (a.total_bounces !== b.total_bounces) {
      return b.total_bounces - a.total_bounces
    }
    if (a.bounces_today !== b.bounces_today) {
      return b.bounces_today - a.bounces_today
    }
    return b.bounces_7d - a.bounces_7d
  })
}